import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  getUserPaymentProfile,
  GetUserPaymentProfileOptions,
  AdminUserPaymentProfileResponse,
  UpdateUserPaymentProfileOptions,
  updateUserPaymentProfile,
} from 'api/payments'

/**
 * Get the payment profile for a user
 * @example useUserPaymentProfile({ userId: 123 }, { retry: true })
 */
export function useUserPaymentProfile(
  /** Pass through to API */
  filters: GetUserPaymentProfileOptions,
  /** useQuery options */
  options?: Pick<
    Parameters<
      typeof useQuery<
        AdminUserPaymentProfileResponse,
        Error,
        AdminUserPaymentProfileResponse,
        string[]
      >
    >[0],
    'retry' | 'initialData' | 'enabled'
  >
): UseQueryResult<AdminUserPaymentProfileResponse> {
  return useQuery({
    queryKey: ['userPaymentProfile', filters.userId.toString()],
    queryFn: async () => getUserPaymentProfile(filters),
    retry: false,
    ...options,
  })
}

export function useUpdateUserPaymentProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (filters: UpdateUserPaymentProfileOptions) =>
      updateUserPaymentProfile(filters),
    onMutate: async (filters) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ['userPaymentProfile', filters.userId.toString()],
      })

      const previousProfile =
        queryClient.getQueryData<AdminUserPaymentProfileResponse>([
          'userPaymentProfile',
          filters.userId.toString(),
        ])

      // Optimistically update to the new value
      queryClient.setQueryData<AdminUserPaymentProfileResponse>(
        ['userPaymentProfile', filters.userId.toString()],
        (old) => {
          if (!old) return old
          return {
            ...old,
            w2PayrollFrequency:
              filters.w2PayrollFrequency ?? old.w2PayrollFrequency,
            w2GarnishmentsNeeded:
              filters.w2GarnishmentsNeeded ?? old.w2GarnishmentsNeeded,
          }
        }
      )

      return { previousProfile }
    },
    onError: (error, filters, context) => {
      // Rollback on error
      if (context?.previousProfile) {
        queryClient.setQueryData(
          ['userPaymentProfile', filters.userId.toString()],
          context.previousProfile
        )
      }
    },
    onSettled: (data, error, filters) => {
      // Invalidate to refetch after mutation
      queryClient.invalidateQueries({
        queryKey: ['userPaymentProfile', filters.userId.toString()],
      })
    },
  })
}
